<template>
    <div class="cart-sidebar-root">
        <transition name="cart-fade">
            <div
                class="cart-sidebar-backdrop"
                v-if="open"
                v-on:click="$emit('close')"
            ></div>
        </transition>
        <transition name="cart-slide">
            <aside
                class="cart-sidebar"
                v-if="open"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cart-sidebar-title"
                v-on:keydown.esc="$emit('close')"
            >
                <div class="cart-sidebar__header">
                    <h2 class="h5 mb-0" id="cart-sidebar-title">
                        {{ $t("Kosár") }}
                        <span class="text-orange ms-1" v-if="items.length">
                            ({{ items.length }})
                        </span>
                    </h2>
                    <button
                        type="button"
                        class="btn-close"
                        ref="close"
                        :aria-label="$t('Bezárás')"
                        v-on:click="$emit('close')"
                    ></button>
                </div>

                <div class="cart-sidebar__body">
                    <div class="cart-sidebar__empty" v-if="!items.length">
                        <p>{{ $t("Jelenleg nincs termék a kosaradban.") }}</p>
                        <router-link
                            :to="{ name: 'Creator' }"
                            class="btn btn-primary"
                            >{{ $t("Matricát tervezek") }}</router-link
                        >
                    </div>
                    <ul class="cart-sidebar__items" v-else>
                        <li
                            class="cart-sidebar__item"
                            v-for="item in items"
                            v-bind:key="'cart-item-' + item.id"
                        >
                            <div class="cart-sidebar__preview">
                                <StickerPreview
                                    :model="item"
                                    :background="item.background"
                                    :sample="item.sample"
                                    :sticker="item.sticker"
                                />
                            </div>
                            <div class="cart-sidebar__info">
                                <div class="d-flex justify-content-between">
                                    <strong v-if="item.sticker">{{
                                        item.sticker.name
                                    }}</strong>
                                    <DeleteCart
                                        :id="item.id"
                                        divClass="delete-item ms-2"
                                    />
                                </div>
                                <div class="small-text">
                                    {{
                                        item.material === 1
                                            ? $t("Műanyag felületekre")
                                            : $t("Textil felületekre")
                                    }}
                                    · {{ $t("30db / csomag") }}
                                </div>
                                <div
                                    class="d-flex justify-content-between mt-1"
                                >
                                    <span class="small-text"
                                        >{{ item.quantity }}
                                        {{ $t("csomag") }}</span
                                    >
                                    <strong>{{ price(item.total) }}</strong>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>

                <div class="cart-sidebar__footer" v-if="items.length">
                    <div class="d-flex justify-content-between mb-3">
                        <span>{{ $t("Részösszeg") }}</span>
                        <strong>{{ price(subtotal) }}</strong>
                    </div>
                    <router-link
                        to="/cart#checkout"
                        class="btn btn-primary mb-2 w-100"
                        >{{ $t("Pénztár") }}</router-link
                    >
                    <router-link
                        to="/cart#cart"
                        class="btn btn-secondary w-100"
                        >{{ $t("Kosár megtekintése") }}</router-link
                    >
                </div>
            </aside>
        </transition>
    </div>
</template>

<script>
import StickerPreview from "./StickerPreview";
import DeleteCart from "./DeleteCart";

const formatter = new Intl.NumberFormat("hu-HU", { maximumFractionDigits: 0 });

export default {
    name: "CartSidebar",
    components: { StickerPreview, DeleteCart },
    props: {
        open: { type: Boolean, default: false },
        cart: { type: Object, default: () => ({ items: [] }) }
    },
    computed: {
        items() {
            return (this.cart && this.cart.items) || [];
        },
        subtotal() {
            return this.items.reduce(
                (sum, item) => sum + (parseFloat(item.total) || 0),
                0
            );
        }
    },
    watch: {
        open(value) {
            document.body.classList.toggle("cart-sidebar-open", value);
            if (value) {
                this.$nextTick(
                    () => this.$refs.close && this.$refs.close.focus()
                );
            }
        }
    },
    mounted() {
        // Escape the header's stacking context so the panel covers fixed banners.
        document.body.appendChild(this.$el);
    },
    beforeDestroy() {
        document.body.classList.remove("cart-sidebar-open");
        if (this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el);
        }
    },
    methods: {
        price(value) {
            return `${formatter.format(parseFloat(value) || 0)} Ft`;
        }
    }
};
</script>

<style>
body.cart-sidebar-open {
    overflow: hidden;
}
.cart-sidebar-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(35, 35, 35, 0.45);
    z-index: 1040;
}
.cart-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(420px, 100vw);
    background: var(--bg, #fff);
    z-index: 1045;
    display: flex;
    flex-direction: column;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.15);
    text-align: left;
}
.cart-sidebar__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid var(--grey, #ced4da);
}
.cart-sidebar__body {
    flex: 1;
    overflow-y: auto;
    padding: 8px 24px;
}
.cart-sidebar__empty {
    text-align: center;
    padding: 48px 0;
}
.cart-sidebar__items {
    list-style: none;
    margin: 0;
    padding: 0;
}
.cart-sidebar__item {
    display: flex;
    gap: 16px;
    padding: 16px 0;
    border-bottom: 1px solid var(--grey, #ced4da);
}
.cart-sidebar__preview {
    width: 110px;
    flex-shrink: 0;
}
.cart-sidebar__preview .preview-holder {
    margin: 0 !important;
}
.cart-sidebar__info {
    flex: 1;
    min-width: 0;
}
.cart-sidebar__info .delete-item {
    cursor: pointer;
}
.cart-sidebar__footer {
    padding: 20px 24px;
    border-top: 1px solid var(--grey, #ced4da);
    background: var(--alt-bg, #f6f2ee);
}
.cart-slide-enter-active,
.cart-slide-leave-active {
    transition: transform 0.25s ease;
}
.cart-slide-enter,
.cart-slide-leave-to {
    transform: translateX(100%);
}
.cart-fade-enter-active,
.cart-fade-leave-active {
    transition: opacity 0.25s ease;
}
.cart-fade-enter,
.cart-fade-leave-to {
    opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
    .cart-slide-enter-active,
    .cart-slide-leave-active,
    .cart-fade-enter-active,
    .cart-fade-leave-active {
        transition: none;
    }
}
</style>
